import React, { useCallback, useMemo } from 'react';
import { Typography, Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import { WbsLabelValueSearchResult } from 'graphql/types';
import { SearchableChip } from 'modules/common/components/Chip/SearchableChip';
import { useAcceptCptSuggestion } from 'modules/timesheet-labels/hooks';
import { ChipProps } from '../schema';
import { useChipState } from '../../../common/hooks/useChipState';
import usePrograms from './usePrograms';
import useProgramOnSelect from './useProgramOnSelect';

const getOptionSelected = (
  option: WbsLabelValueSearchResult,
  selected: WbsLabelValueSearchResult
) => option.id === selected?.id;

export const ProgramChip: React.FC<ChipProps> = ({
  dependantLabels,
  onSelect,
  disabled = false
}: ChipProps) => {
  const { label, options, handleSearch, loading, hasMore } = usePrograms(
    dependantLabels
  );
  const { formatMessage } = useIntl();

  const customOptions = useMemo(() => {
    return options.map((item) => {
      if (item.id === null) {
        const label = formatMessage({ id: 'labelType.PROGRAM' });
        const text = formatMessage(
          { id: 'labelType.NoLabel' },
          {
            Label: label
          }
        );
        return { ...item, displayText: text };
      }
      return item;
    });
  }, [formatMessage, options]);

  const onProgramSelect = useProgramOnSelect(onSelect, dependantLabels);
  const { isEmptyValue: isEmpty } = useChipState(label);

  const openHandler = useCallback(() => {
    handleSearch('');
  }, [handleSearch]);

  const onChipDelete = useCallback(() => {
    onProgramSelect(null);
  }, [onProgramSelect]);

  const getOptionLabel = useCallback(
    (option: WbsLabelValueSearchResult) => option.displayText || '',
    []
  );

  const defaultRenderOption = useCallback(
    (option: WbsLabelValueSearchResult) => {
      return (
        <Tooltip
          followCursor={true}
          title={option.displayText}
          disableInteractive
        >
          <Typography variant="inherit" noWrap>
            {option.displayText}
          </Typography>
        </Tooltip>
      );
    },
    []
  );

  const handleDelete = useMemo(() => {
    return !isEmpty ? { handleDelete: onChipDelete } : null;
  }, [isEmpty, onChipDelete]);

  const acceptProgramSuggestion = useAcceptCptSuggestion(
    dependantLabels,
    'program'
  );

  const value = useMemo(
    () =>
      label?.value
        ? {
            id: label.value.idValue,
            displayText: label.value.stringValue ?? ''
          }
        : undefined,
    [label]
  );

  return (
    <SearchableChip
      label={label}
      onOpen={openHandler}
      handleSearch={handleSearch}
      loading={loading}
      options={customOptions}
      onSelect={onProgramSelect}
      disabled={disabled}
      getOptionLabel={getOptionLabel}
      renderOption={defaultRenderOption}
      {...handleDelete}
      getOptionSelected={getOptionSelected}
      value={value}
      hasMore={hasMore}
      acceptSuggestion={acceptProgramSuggestion}
    />
  );
};

export default ProgramChip;
